import {
  type InstallmentCouponPreviewScenario,
  selectInstallmentCouponPaymentAmount,
} from "./installmentCouponPaymentSelection";

export interface InstallmentCoupon {
  amount: number;
  duration: string;
  type: string;
}

export interface InstallmentPurchaseValueInput {
  coupon?: InstallmentCoupon;
  currencyPrecision: number;
  installmentsCount: number;
  previewScenario: InstallmentCouponPreviewScenario;
  upfrontPaymentAmount: number;
  value: number;
}

const MAX_INSTALLMENTS = 50;
const COUPON_FIXED = "PaywallCoupons::Fixed";
const COUPON_PERCENT = "PaywallCoupons::Percent";
const MAX_PERCENT_HUNDREDTHS = 10_000;
const PERCENT_PRECISION = 2;

const decimalAmountInScaledUnits = (
  amount: number,
  precision: number,
): number => {
  const [whole, fraction = ""] = Number(amount).toString().split(".");
  const fractionalUnits = Number(
    fraction.padEnd(precision, "0").slice(0, precision),
  );

  return Number(whole) * 10 ** precision + fractionalUnits;
};

const isValidInstallmentPlan = ({
  value,
  upfrontPaymentAmount,
  installmentsCount,
}: InstallmentPurchaseValueInput): boolean =>
  Number.isSafeInteger(value) &&
  value >= 0 &&
  Number.isSafeInteger(upfrontPaymentAmount) &&
  upfrontPaymentAmount >= 0 &&
  upfrontPaymentAmount <= value &&
  Number.isInteger(installmentsCount) &&
  installmentsCount >= 1 &&
  installmentsCount <= MAX_INSTALLMENTS;

const isValidCoupon = (coupon: InstallmentCoupon) =>
  Number.isFinite(Number(coupon.amount)) &&
  Number(coupon.amount) >= 0 &&
  (coupon.type === COUPON_FIXED ||
    (coupon.type === COUPON_PERCENT && Number(coupon.amount) <= 100));

const onceDiscountedTotal = (
  value: number,
  firstPaidAmount: number,
  discount: (amount: number) => number,
) => Math.max(value - discount(firstPaidAmount), 0);

const foreverFixedDiscountedTotal = (
  value: number,
  upfrontPaymentAmount: number,
  installmentAmount: number,
  installmentsCount: number,
  discount: (amount: number) => number,
) => {
  const paymentAmounts = [
    ...(upfrontPaymentAmount > 0 ? [upfrontPaymentAmount] : []),
    ...Array.from({ length: installmentsCount }, () => installmentAmount),
  ];
  const discounts = paymentAmounts.map(discount);
  if (discounts.every((amount, index) => amount >= paymentAmounts[index]))
    return 0;

  return Math.max(
    value - discounts.reduce((sum, amount) => sum + amount, 0),
    0,
  );
};

export const calculateInstallmentPurchaseValue = (
  input: InstallmentPurchaseValueInput,
): number | null => {
  const {
    value,
    upfrontPaymentAmount,
    installmentsCount,
    currencyPrecision,
    coupon,
  } = input;
  if (!isValidInstallmentPlan(input)) return null;
  if (!coupon) return value;
  if (!isValidCoupon(coupon)) return null;

  const installmentAmount = Math.floor(
    (value - upfrontPaymentAmount) / installmentsCount,
  );
  const couponAmount =
    coupon.type === COUPON_FIXED
      ? decimalAmountInScaledUnits(coupon.amount, currencyPrecision)
      : decimalAmountInScaledUnits(coupon.amount, PERCENT_PRECISION);
  const discount = (amount: number) =>
    coupon.type === COUPON_PERCENT
      ? Number(
          (BigInt(amount) * BigInt(couponAmount)) /
            BigInt(MAX_PERCENT_HUNDREDTHS),
        )
      : Math.min(amount, couponAmount);

  if (coupon.duration === "once") {
    return onceDiscountedTotal(
      value,
      selectInstallmentCouponPaymentAmount({
        previewScenario: input.previewScenario,
        upfrontPaymentAmount,
        installmentAmount,
      }),
      discount,
    );
  }

  if (coupon.duration === "forever" && coupon.type === COUPON_PERCENT) {
    return Math.max(value - discount(value), 0);
  }

  if (coupon.duration === "forever" && coupon.type === COUPON_FIXED) {
    return foreverFixedDiscountedTotal(
      value,
      upfrontPaymentAmount,
      installmentAmount,
      installmentsCount,
      discount,
    );
  }

  return coupon.duration === "repeating" ? value : null;
};
